/**
 * PlayerExpressionPanel Component
 * 
 * Allows players to customize their identity:
 * - Titles (earned through achievements)
 * - Ship flags (patterns, emblems, colors)
 * - Player bio
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';

// ============ TYPES ============

interface TitleDefinition {
  id: string;
  name: string;
  description: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  color: string;
  requirement: {
    type: string;
    value: number;
    description: string;
  };
}

interface FlagPattern {
  id: string;
  name: string;
  description: string;
  pattern: string;
  unlockRequirement?: { type: string; value: number };
}

interface FlagEmblem {
  id: string;
  name: string;
  description: string;
  symbol: string;
  unlockRequirement?: { type: string; value: number };
}

interface ExpressionData {
  unlockedTitles: string[];
  equippedTitle: string | null;
  equippedTitleData: TitleDefinition | null;
  flagPattern: string;
  flagPrimaryColor: string;
  flagSecondaryColor: string;
  flagEmblem: string;
  flagEmblemColor: string;
  showFlagAbovePlayer: boolean;
  bio: string;
  stats: {
    totalKills: number;
    totalDeaths: number;
    islandsClaimed: number;
    totalPiecesEarned: number;
    shipsSunk: number;
    fishCaught: number;
    resourcesHarvested: number;
    battlesWon: number;
    playtimeMinutes: number;
  };
}

interface PlayerExpressionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  socket: Socket | null;
  playerLevel: number;
  playerInfamy: number;
}

// ============ CONSTANTS ============

const RARITY_COLORS: Record<string, string> = {
  common: '#8b949e',
  uncommon: '#3fb950',
  rare: '#58a6ff',
  epic: '#a371f7',
  legendary: '#ffd700',
};

const PRESET_COLORS = [
  // Dark blues/purples
  '#1a1a2e', '#16213e', '#0f3460', '#1b1b2f', '#162447', '#1f4068', '#2c2c54', '#474787',
  // Reds/oranges
  '#e94560', '#e43f5a', '#c0392b', '#e74c3c', '#d35400', '#e67e22', '#ff6b6b', '#ff4757',
  // Yellows/golds
  '#f39c12', '#f1c40f', '#ffd700', '#ffcc00', '#f9ca24', '#ffc312',
  // Greens
  '#27ae60', '#2ecc71', '#16a085', '#1abc9c', '#00b894', '#00cec9', '#55a630', '#2d6a4f',
  // Blues
  '#2980b9', '#3498db', '#0984e3', '#74b9ff', '#48dbfb', '#0abde3', '#00a8ff', '#1e90ff',
  // Purples/pinks
  '#8e44ad', '#9b59b6', '#a371f7', '#6c5ce7', '#fd79a8', '#e84393', '#be2edd', '#8854d0',
  // Grays/neutrals
  '#2c3e50', '#34495e', '#7f8c8d', '#95a5a6', '#636e72', '#b2bec3',
  // Black/white
  '#ffffff', '#f5f5f5', '#e0e0e0', '#000000', '#1a1a1a', '#333333', '#4a4a4a', '#666666',
];

// ============ COMPONENT ============

export const PlayerExpressionPanel: React.FC<PlayerExpressionPanelProps> = ({
  isOpen,
  onClose,
  socket,
  playerLevel,
  playerInfamy,
}) => {
  const [activeTab, setActiveTab] = useState<'titles' | 'flag' | 'bio'>('titles');
  const [expressionData, setExpressionData] = useState<ExpressionData | null>(null);
  const [titleDefinitions, setTitleDefinitions] = useState<Record<string, TitleDefinition>>({});
  const [flagPatterns, setFlagPatterns] = useState<Record<string, FlagPattern>>({});
  const [flagEmblems, setFlagEmblems] = useState<Record<string, FlagEmblem>>({});
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [bioText, setBioText] = useState<string>('');
  const [selectedPattern, setSelectedPattern] = useState<string>('solid');
  const [selectedEmblem, setSelectedEmblem] = useState<string>('skull');
  const [primaryColor, setPrimaryColor] = useState<string>('#1a1a2e');
  const [secondaryColor, setSecondaryColor] = useState<string>('#4a5568');
  const [emblemColor, setEmblemColor] = useState<string>('#ffffff');
  const [showFlagAbovePlayer, setShowFlagAbovePlayer] = useState<boolean>(false);
  const { handleHeaderMouseDown, style: dragStyle } = useDraggablePanel(200, 100);

  // Fetch data on open
  useEffect(() => {
    if (!socket || !isOpen) return;

    const handleExpressionData = (data: ExpressionData): void => {
      setExpressionData(data);
      setBioText(data.bio || '');
      setSelectedPattern(data.flagPattern);
      setSelectedEmblem(data.flagEmblem);
      setPrimaryColor(data.flagPrimaryColor);
      setSecondaryColor(data.flagSecondaryColor);
      setEmblemColor(data.flagEmblemColor);
      setShowFlagAbovePlayer(data.showFlagAbovePlayer || false);
    };

    const handleTitles = (data: any): void => {
      if (!data || !data.definitions || typeof data.definitions !== 'object') {
        console.error('❌ Invalid title definitions received:', data);
        setMessage({ text: 'Failed to load titles', type: 'error' });
        return;
      }
      console.log(`📚 Received ${Object.keys(data.definitions).length} title definitions`);
      setTitleDefinitions(data.definitions);
    };

    const handleFlagOptions = (data: any): void => {
      if (!data || !data.patterns || !data.emblems || typeof data.patterns !== 'object' || typeof data.emblems !== 'object') {
        console.error('❌ Invalid flag options received:', data);
        setMessage({ text: 'Failed to load flag options', type: 'error' });
        return;
      }
      console.log(`🚩 Received ${Object.keys(data.patterns).length} patterns, ${Object.keys(data.emblems).length} emblems`);
      setFlagPatterns(data.patterns);
      setFlagEmblems(data.emblems);
    };

    const handleTitleEquipped = (data: { titleId: string; message: string }): void => {
      setMessage({ text: data.message, type: 'success' });
      socket.emit('expression:get');
      setTimeout(() => setMessage(null), 3000);
    };

    const handleTitleUnequipped = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'success' });
      socket.emit('expression:get');
      setTimeout(() => setMessage(null), 3000);
    };

    const handleFlagUpdated = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'success' });
      socket.emit('expression:get');
      setTimeout(() => setMessage(null), 3000);
    };

    const handleBioUpdated = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'success' });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleError = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'error' });
      setTimeout(() => setMessage(null), 3000);
    };

    // Register listeners BEFORE emitting requests
    socket.on('expression:data', handleExpressionData);
    socket.on('expression:titles', handleTitles);
    socket.on('expression:flagOptions', handleFlagOptions);
    socket.on('expression:titleEquipped', handleTitleEquipped);
    socket.on('expression:titleUnequipped', handleTitleUnequipped);
    socket.on('expression:flagUpdated', handleFlagUpdated);
    socket.on('expression:bioUpdated', handleBioUpdated);
    socket.on('expression:error', handleError);

    // Now emit requests after listeners are registered
    socket.emit('expression:get');
    socket.emit('expression:getTitles');
    socket.emit('expression:getFlagOptions');

    return () => {
      socket.off('expression:data', handleExpressionData);
      socket.off('expression:titles', handleTitles);
      socket.off('expression:flagOptions', handleFlagOptions);
      socket.off('expression:titleEquipped', handleTitleEquipped);
      socket.off('expression:titleUnequipped', handleTitleUnequipped);
      socket.off('expression:flagUpdated', handleFlagUpdated);
      socket.off('expression:bioUpdated', handleBioUpdated);
      socket.off('expression:error', handleError);
    };
  }, [socket, isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleEquipTitle = useCallback((titleId: string): void => {
    if (!socket) return;
    socket.emit('expression:equipTitle', { titleId });
  }, [socket]);

  const handleUnequipTitle = useCallback((): void => {
    if (!socket) return;
    socket.emit('expression:unequipTitle');
  }, [socket]);

  const handleUpdateFlag = useCallback((): void => {
    if (!socket) return;
    socket.emit('expression:updateFlag', {
      pattern: selectedPattern,
      primaryColor,
      secondaryColor,
      emblem: selectedEmblem,
      emblemColor,
      showFlagAbovePlayer,
    });
  }, [socket, selectedPattern, primaryColor, secondaryColor, selectedEmblem, emblemColor, showFlagAbovePlayer]);

  const handleUpdateBio = useCallback((): void => {
    if (!socket) return;
    socket.emit('expression:updateBio', { bio: bioText });
  }, [socket, bioText]);

  const isPatternUnlocked = (pattern: FlagPattern): boolean => {
    if (!pattern.unlockRequirement) return true;
    if (pattern.unlockRequirement.type === 'level') {
      return playerLevel >= pattern.unlockRequirement.value;
    }
    return true;
  };

  const isEmblemUnlocked = (emblem: FlagEmblem): boolean => {
    if (!emblem.unlockRequirement) return true;
    const req = emblem.unlockRequirement;
    if (req.type === 'level') return playerLevel >= req.value;
    if (req.type === 'kills' && expressionData) return expressionData.stats.totalKills >= req.value;
    if (req.type === 'pieces_earned' && expressionData) return expressionData.stats.totalPiecesEarned >= req.value;
    return true;
  };

  const isTitleUnlocked = (titleId: string): boolean => {
    return expressionData?.unlockedTitles.includes(titleId) || false;
  };

  const getTitleProgress = (title: TitleDefinition): { current: number; required: number; percent: number } => {
    if (!expressionData) return { current: 0, required: title.requirement.value, percent: 0 };
    
    let current = 0;
    const required = title.requirement.value;
    
    switch (title.requirement.type) {
      case 'level': current = playerLevel; break;
      case 'kills': current = expressionData.stats.totalKills; break;
      case 'deaths': current = expressionData.stats.totalDeaths; break;
      case 'islands_claimed': current = expressionData.stats.islandsClaimed; break;
      case 'pieces_earned': current = expressionData.stats.totalPiecesEarned; break;
      case 'ships_sunk': current = expressionData.stats.shipsSunk; break;
      case 'fish_caught': current = expressionData.stats.fishCaught; break;
      case 'resources_harvested': current = expressionData.stats.resourcesHarvested; break;
      case 'battles_won': current = expressionData.stats.battlesWon; break;
      case 'infamy': current = playerInfamy; break;
      case 'playtime': current = expressionData.stats.playtimeMinutes; break;
    }
    
    const percent = Math.min(100, (current / required) * 100);
    return { current, required, percent };
  };

  const renderEmblemSVG = (symbol: string, color: string, size: number = 24): React.ReactNode => {
    const svgProps = { width: size, height: size, viewBox: '0 0 24 24', fill: color };
    
    switch (symbol) {
      case 'skull':
        return (
          <svg {...svgProps}>
            <circle cx="12" cy="10" r="7" fill="none" stroke={color} strokeWidth="2"/>
            <circle cx="9" cy="9" r="1.5" fill={color}/>
            <circle cx="15" cy="9" r="1.5" fill={color}/>
            <path d="M9 14 L12 17 L15 14" fill="none" stroke={color} strokeWidth="1.5"/>
            <rect x="10" y="17" width="4" height="5" fill={color}/>
          </svg>
        );
      case 'anchor':
        return (
          <svg {...svgProps}>
            <circle cx="12" cy="5" r="2" fill="none" stroke={color} strokeWidth="2"/>
            <line x1="12" y1="7" x2="12" y2="20" stroke={color} strokeWidth="2"/>
            <path d="M6 16 Q12 22 18 16" fill="none" stroke={color} strokeWidth="2"/>
            <line x1="8" y1="10" x2="16" y2="10" stroke={color} strokeWidth="2"/>
          </svg>
        );
      case 'star':
        return (
          <svg {...svgProps}>
            <polygon points="12,2 15,9 22,9 17,14 19,22 12,17 5,22 7,14 2,9 9,9" fill={color}/>
          </svg>
        );
      case 'compass':
        return (
          <svg {...svgProps}>
            <circle cx="12" cy="12" r="9" fill="none" stroke={color} strokeWidth="2"/>
            <polygon points="12,4 14,12 12,20 10,12" fill={color}/>
            <polygon points="4,12 12,10 20,12 12,14" fill={color} opacity="0.6"/>
          </svg>
        );
      case 'ship':
        return (
          <svg {...svgProps}>
            <path d="M4 18 L12 22 L20 18 L18 14 L6 14 Z" fill={color}/>
            <rect x="11" y="6" width="2" height="8" fill={color}/>
            <polygon points="13,6 13,12 19,12" fill={color} opacity="0.8"/>
          </svg>
        );
      case 'wave':
        return (
          <svg {...svgProps}>
            <path d="M2 12 Q5 8 8 12 Q11 16 14 12 Q17 8 20 12 Q23 16 26 12" fill="none" stroke={color} strokeWidth="2"/>
            <path d="M2 17 Q5 13 8 17 Q11 21 14 17 Q17 13 20 17" fill="none" stroke={color} strokeWidth="2" opacity="0.6"/>
          </svg>
        );
      case 'diamond':
        return (
          <svg {...svgProps}>
            <polygon points="12,2 22,12 12,22 2,12" fill={color}/>
          </svg>
        );
      case 'shield':
        return (
          <svg {...svgProps}>
            <path d="M12 2 L20 6 L20 12 Q20 18 12 22 Q4 18 4 12 L4 6 Z" fill={color}/>
          </svg>
        );
      case 'lightning':
        return (
          <svg {...svgProps}>
            <polygon points="13,2 6,13 11,13 10,22 18,10 13,10" fill={color}/>
          </svg>
        );
      case 'crown':
        return (
          <svg {...svgProps}>
            <path d="M4 18 L4 10 L8 14 L12 6 L16 14 L20 10 L20 18 Z" fill={color}/>
            <rect x="4" y="18" width="16" height="3" fill={color}/>
          </svg>
        );
      case 'sword':
        return (
          <svg {...svgProps}>
            <line x1="4" y1="20" x2="18" y2="6" stroke={color} strokeWidth="2"/>
            <line x1="4" y1="6" x2="18" y2="20" stroke={color} strokeWidth="2"/>
            <rect x="10" y="10" width="4" height="4" fill={color}/>
          </svg>
        );
      case 'coin':
        return (
          <svg {...svgProps}>
            <circle cx="12" cy="12" r="9" fill={color}/>
            <circle cx="12" cy="12" r="6" fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="1"/>
            <text x="12" y="16" textAnchor="middle" fontSize="10" fill="rgba(0,0,0,0.5)">P</text>
          </svg>
        );
      case 'trident':
        return (
          <svg {...svgProps}>
            <line x1="12" y1="22" x2="12" y2="6" stroke={color} strokeWidth="2"/>
            <path d="M6 10 L6 4 L8 6 L8 10" fill="none" stroke={color} strokeWidth="2"/>
            <path d="M18 10 L18 4 L16 6 L16 10" fill="none" stroke={color} strokeWidth="2"/>
            <path d="M12 6 L12 2" stroke={color} strokeWidth="2"/>
          </svg>
        );
      case 'dragon':
        return (
          <svg {...svgProps}>
            <path d="M4 16 Q8 12 12 14 Q16 16 20 12 L18 8 Q14 10 12 8 Q10 6 6 8 L4 12 Z" fill={color}/>
            <circle cx="7" cy="10" r="1" fill="rgba(0,0,0,0.5)"/>
            <path d="M4 16 L2 20 L6 18" fill={color}/>
          </svg>
        );
      case 'phoenix':
        return (
          <svg {...svgProps}>
            <path d="M12 4 Q16 8 14 12 Q18 10 20 14 Q16 16 12 14 Q8 16 4 14 Q6 10 10 12 Q8 8 12 4" fill={color}/>
            <path d="M10 14 L8 20 M14 14 L16 20 M12 14 L12 22" stroke={color} strokeWidth="1.5"/>
          </svg>
        );
      case 'kraken':
        return (
          <svg {...svgProps}>
            <circle cx="12" cy="8" r="5" fill={color}/>
            <path d="M7 12 Q5 18 4 20" stroke={color} strokeWidth="2" fill="none"/>
            <path d="M9 13 Q8 18 7 22" stroke={color} strokeWidth="2" fill="none"/>
            <path d="M12 13 L12 22" stroke={color} strokeWidth="2"/>
            <path d="M15 13 Q16 18 17 22" stroke={color} strokeWidth="2" fill="none"/>
            <path d="M17 12 Q19 18 20 20" stroke={color} strokeWidth="2" fill="none"/>
          </svg>
        );
      case 'moon':
        return (
          <svg {...svgProps}>
            <path d="M16 4 A9 9 0 1 0 16 20 A7 7 0 1 1 16 4" fill={color}/>
          </svg>
        );
      case 'sun':
        return (
          <svg {...svgProps}>
            <circle cx="12" cy="12" r="5" fill={color}/>
            <line x1="12" y1="2" x2="12" y2="5" stroke={color} strokeWidth="2"/>
            <line x1="12" y1="19" x2="12" y2="22" stroke={color} strokeWidth="2"/>
            <line x1="2" y1="12" x2="5" y2="12" stroke={color} strokeWidth="2"/>
            <line x1="19" y1="12" x2="22" y2="12" stroke={color} strokeWidth="2"/>
            <line x1="4.9" y1="4.9" x2="7" y2="7" stroke={color} strokeWidth="2"/>
            <line x1="17" y1="17" x2="19.1" y2="19.1" stroke={color} strokeWidth="2"/>
            <line x1="4.9" y1="19.1" x2="7" y2="17" stroke={color} strokeWidth="2"/>
            <line x1="17" y1="7" x2="19.1" y2="4.9" stroke={color} strokeWidth="2"/>
          </svg>
        );
      case 'heart':
        return (
          <svg {...svgProps}>
            <path d="M12 21 L4 13 Q2 10 4 7 Q6 4 10 6 L12 8 L14 6 Q18 4 20 7 Q22 10 20 13 Z" fill={color}/>
          </svg>
        );
      case 'crossbones':
        return (
          <svg {...svgProps}>
            <line x1="4" y1="4" x2="20" y2="20" stroke={color} strokeWidth="3"/>
            <line x1="20" y1="4" x2="4" y2="20" stroke={color} strokeWidth="3"/>
            <circle cx="4" cy="4" r="2" fill={color}/>
            <circle cx="20" cy="4" r="2" fill={color}/>
            <circle cx="4" cy="20" r="2" fill={color}/>
            <circle cx="20" cy="20" r="2" fill={color}/>
          </svg>
        );
      case 'flag':
        return (
          <svg {...svgProps}>
            <line x1="6" y1="4" x2="6" y2="22" stroke={color} strokeWidth="2"/>
            <path d="M6 4 L20 8 L6 12 Z" fill={color}/>
          </svg>
        );
      default:
        return null;
    }
  };

   const renderFlagPreview = (): React.ReactNode => {
     const pattern = (flagPatterns ?? {})[selectedPattern];
     const emblem = (flagEmblems ?? {})[selectedEmblem];
    
    return (
      <div
        style={{
          width: 120,
          height: 80,
          border: '2px solid #30363d',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background pattern */}
        {pattern?.pattern === 'solid' && (
          <div style={{ width: '100%', height: '100%', backgroundColor: primaryColor }} />
        )}
        {pattern?.pattern === 'striped_h' && (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, backgroundColor: primaryColor }} />
            <div style={{ flex: 1, backgroundColor: secondaryColor }} />
            <div style={{ flex: 1, backgroundColor: primaryColor }} />
          </div>
        )}
        {pattern?.pattern === 'striped_v' && (
          <div style={{ width: '100%', height: '100%', display: 'flex' }}>
            <div style={{ flex: 1, backgroundColor: primaryColor }} />
            <div style={{ flex: 1, backgroundColor: secondaryColor }} />
            <div style={{ flex: 1, backgroundColor: primaryColor }} />
          </div>
        )}
        {pattern?.pattern === 'quartered' && (
          <div style={{ width: '100%', height: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
            <div style={{ backgroundColor: primaryColor }} />
            <div style={{ backgroundColor: secondaryColor }} />
            <div style={{ backgroundColor: secondaryColor }} />
            <div style={{ backgroundColor: primaryColor }} />
          </div>
        )}
        {pattern?.pattern === 'diagonal' && (
          <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${primaryColor} 50%, ${secondaryColor} 50%)` }} />
        )}
        {pattern?.pattern === 'cross' && (
          <div style={{ width: '100%', height: '100%', backgroundColor: primaryColor, position: 'relative' }}>
            <div style={{ position: 'absolute', top: '35%', left: 0, right: 0, height: '30%', backgroundColor: secondaryColor }} />
            <div style={{ position: 'absolute', left: '40%', top: 0, bottom: 0, width: '20%', backgroundColor: secondaryColor }} />
          </div>
        )}
        {pattern?.pattern === 'saltire' && (
          <div style={{ width: '100%', height: '100%', backgroundColor: primaryColor, position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: `linear-gradient(45deg, transparent 40%, ${secondaryColor} 40%, ${secondaryColor} 60%, transparent 60%), linear-gradient(-45deg, transparent 40%, ${secondaryColor} 40%, ${secondaryColor} 60%, transparent 60%)` }} />
          </div>
        )}
        {pattern?.pattern === 'bordered' && (
          <div style={{ width: '100%', height: '100%', backgroundColor: secondaryColor, padding: 8, boxSizing: 'border-box' }}>
            <div style={{ width: '100%', height: '100%', backgroundColor: primaryColor }} />
          </div>
        )}
        
        {/* Emblem */}
        {emblem && emblem.symbol && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              }}
          >
            {renderEmblemSVG(emblem.symbol, emblemColor, 32)}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 1000,
        ...dragStyle,
        backgroundColor: '#0d1117',
        border: '2px solid #30363d',
        padding: 0,
        width: '600px',
        maxHeight: '85vh',
        overflow: 'hidden',
        fontFamily: '"Sometype Mono", monospace',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        onMouseDown={handleHeaderMouseDown}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid #30363d',
          backgroundColor: '#161b22',
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
          <span style={{ color: '#a371f7', fontSize: '14px', fontWeight: 'bold' }}>
            PLAYER IDENTITY
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '4px 8px',
              fontFamily: '"Sometype Mono", monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          {(['titles', 'flag', 'bio'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: activeTab === tab ? '#0d1117' : 'transparent',
                color: activeTab === tab ? '#a371f7' : '#8b949e',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #a371f7' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold',
                fontFamily: '"Sometype Mono", monospace',
                textTransform: 'uppercase',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              padding: '10px 16px',
              backgroundColor: message.type === 'success' ? '#1c3d2e' : '#3d1c1c',
              borderBottom: `1px solid ${message.type === 'success' ? '#3fb950' : '#f85149'}`,
              color: message.type === 'success' ? '#3fb950' : '#f85149',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            {message.text}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {/* Titles Tab */}
          {activeTab === 'titles' && (
            <div>
              {/* Current Title */}
              {expressionData?.equippedTitleData && (
                <div
                  style={{
                    backgroundColor: '#161b22',
                    border: `2px solid ${expressionData.equippedTitleData.color}`,
                    padding: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '4px' }}>
                    EQUIPPED TITLE
                  </div>
                  <div style={{ color: expressionData.equippedTitleData.color, fontSize: '16px', fontWeight: 'bold' }}>
                    {expressionData.equippedTitleData.name}
                  </div>
                  <div style={{ color: '#8b949e', fontSize: '11px', marginTop: '4px' }}>
                    {expressionData.equippedTitleData.description}
                  </div>

                  <button
                    type="button"
                    onClick={handleUnequipTitle}
                    style={{
                      marginTop: 10,
                      padding: '6px 12px',
                      backgroundColor: '#21262d',
                      color: '#e5e7eb',
                      border: '1px solid #30363d',
                      cursor: 'pointer',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    UNEQUIP TITLE
                  </button>
                </div>
              )}

              {/* All Titles */}
               <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                 ALL TITLES ({expressionData?.unlockedTitles.length || 0} / {Object.keys(titleDefinitions ?? {}).length} UNLOCKED)
               </div>
               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                 {Object.values(titleDefinitions ?? {}).map((title) => {
                  const unlocked = isTitleUnlocked(title.id);
                  const equipped = expressionData?.equippedTitle === title.id;
                  const progress = getTitleProgress(title);
                  
                  if (unlocked && !equipped) {
                    console.log(`✅ Title "${title.name}" is unlocked and not equipped - should show EQUIP button`);
                  }

                  return (
                    <div
                      key={title.id}
                      style={{
                        backgroundColor: equipped ? '#1c2128' : '#161b22',
                        border: `1px solid ${equipped ? title.color : unlocked ? '#30363d' : '#21262d'}`,
                        padding: '10px',
                        opacity: unlocked ? 1 : 0.6,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span
                              style={{
                                color: title.color,
                                fontSize: '13px',
                                fontWeight: 'bold',
                              }}
                            >
                              {title.name}
                            </span>
                            <span
                              style={{
                                backgroundColor: RARITY_COLORS[title.rarity],
                                color: '#0d1117',
                                fontSize: '8px',
                                padding: '2px 4px',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                              }}
                            >
                              {title.rarity}
                            </span>
                            {equipped && (
                              <span style={{ color: '#3fb950', fontSize: '9px' }}>[EQUIPPED]</span>
                            )}
                          </div>
                          <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '4px' }}>
                            {title.description}
                          </div>
                          <div style={{ color: '#6e7681', fontSize: '9px' }}>
                            {title.requirement.description}
                          </div>
                          
                          {/* Progress bar for locked titles */}
                          {!unlocked && (
                            <div style={{ marginTop: '6px' }}>
                              <div
                                style={{
                                  height: '4px',
                                  backgroundColor: '#21262d',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    height: '100%',
                                    width: `${progress.percent}%`,
                                    backgroundColor: title.color,
                                  }}
                                />
                              </div>
                              <div style={{ color: '#6e7681', fontSize: '8px', marginTop: '2px' }}>
                                {progress.current.toLocaleString()} / {progress.required.toLocaleString()}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {unlocked && !equipped && (
                          <button
                            onClick={() => handleEquipTitle(title.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#238636',
                              color: '#ffffff',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              fontFamily: '"Sometype Mono", monospace',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            EQUIP
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Flag Tab */}
          {activeTab === 'flag' && (
            <div>
              {/* Preview */}
              <div
                style={{
                  backgroundColor: '#161b22',
                  border: '1px solid #30363d',
                  padding: '16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <div>
                  <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '8px' }}>
                    FLAG PREVIEW
                  </div>
                  {renderFlagPreview()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#c9d1d9', fontSize: '12px', marginBottom: '4px' }}>
                    Your ship's flag represents your identity on the seas.
                  </div>
                  <div style={{ color: '#8b949e', fontSize: '10px' }}>
                    Unlock more patterns and emblems as you level up.
                  </div>
                </div>
              </div>

              {/* Pattern Selection */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                  PATTERN
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                   {Object.values(flagPatterns ?? {}).map((pattern) => {
                    const unlocked = isPatternUnlocked(pattern);
                    const selected = selectedPattern === pattern.id;

                    return (
                      <button
                        key={pattern.id}
                        onClick={() => unlocked && setSelectedPattern(pattern.id)}
                        disabled={!unlocked}
                        title={unlocked ? pattern.name : `Requires Level ${pattern.unlockRequirement?.value}`}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: selected ? '#238636' : unlocked ? '#21262d' : '#161b22',
                          color: selected ? '#ffffff' : unlocked ? '#c9d1d9' : '#6e7681',
                          border: selected ? '2px solid #3fb950' : '1px solid #30363d',
                          cursor: unlocked ? 'pointer' : 'not-allowed',
                          fontSize: '10px',
                          fontFamily: '"Sometype Mono", monospace',
                          opacity: unlocked ? 1 : 0.5,
                        }}
                      >
                        {pattern.name}
                        {!unlocked && pattern.unlockRequirement && (
                          <span style={{ marginLeft: '4px', color: '#f0883e' }}>
                            [Lv{pattern.unlockRequirement.value}]
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Emblem Selection */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                  EMBLEM
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                   {Object.values(flagEmblems ?? {}).map((emblem) => {
                    const unlocked = isEmblemUnlocked(emblem);
                    const selected = selectedEmblem === emblem.id;

                    return (
                      <button
                        key={emblem.id}
                        onClick={() => unlocked && setSelectedEmblem(emblem.id)}
                        disabled={!unlocked}
                        title={unlocked ? emblem.name : `Locked`}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: selected ? '#238636' : unlocked ? '#21262d' : '#161b22',
                          color: selected ? '#ffffff' : unlocked ? '#c9d1d9' : '#6e7681',
                          border: selected ? '2px solid #3fb950' : '1px solid #30363d',
                          cursor: unlocked ? 'pointer' : 'not-allowed',
                          fontSize: '10px',
                          fontFamily: '"Sometype Mono", monospace',
                          opacity: unlocked ? 1 : 0.5,
                          minWidth: '60px',
                        }}
                      >
                        {emblem.symbol && (
                          <span style={{ marginRight: '4px', display: 'inline-flex', verticalAlign: 'middle' }}>
                            {renderEmblemSVG(emblem.symbol, selected ? '#ffffff' : unlocked ? '#c9d1d9' : '#6e7681', 14)}
                          </span>
                        )}
                        {emblem.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color Selection */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {/* Primary Color */}
                <div>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    PRIMARY COLOR
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxWidth: '140px' }}>
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={`primary-${color}`}
                        onClick={() => setPrimaryColor(color)}
                        style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: color,
                          border: primaryColor === color ? '2px solid #ffffff' : '1px solid #30363d',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Secondary Color */}
                <div>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    SECONDARY COLOR
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxWidth: '140px' }}>
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={`secondary-${color}`}
                        onClick={() => setSecondaryColor(color)}
                        style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: color,
                          border: secondaryColor === color ? '2px solid #ffffff' : '1px solid #30363d',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Emblem Color */}
                <div>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    EMBLEM COLOR
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxWidth: '140px' }}>
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={`emblem-${color}`}
                        onClick={() => setEmblemColor(color)}
                        style={{
                          width: '16px',
                          height: '16px',
                          backgroundColor: color,
                          border: emblemColor === color ? '2px solid #ffffff' : '1px solid #30363d',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Show Flag Above Player Toggle */}
              <div
                style={{
                  backgroundColor: '#161b22',
                  border: '1px solid #30363d',
                  padding: '12px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ color: '#c9d1d9', fontSize: '11px', fontWeight: 'bold' }}>
                    SHOW FLAG ABOVE PLAYER
                  </div>
                  <div style={{ color: '#8b949e', fontSize: '9px', marginTop: '2px' }}>
                    Display your flag above your ship at all times
                  </div>
                </div>
                <button
                  onClick={() => setShowFlagAbovePlayer(!showFlagAbovePlayer)}
                  style={{
                    width: '48px',
                    height: '24px',
                    backgroundColor: showFlagAbovePlayer ? '#238636' : '#21262d',
                    border: '1px solid #30363d',
                    cursor: 'pointer',
                    position: 'relative',
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#ffffff',
                      position: 'absolute',
                      top: '1px',
                      left: showFlagAbovePlayer ? '25px' : '2px',
                      transition: 'left 0.2s',
                    }}
                  />
                </button>
              </div>

              {/* Save Button */}
              <button
                onClick={handleUpdateFlag}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#238636',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  fontFamily: '"Sometype Mono", monospace',
                }}
              >
                SAVE FLAG
              </button>
            </div>
          )}

          {/* Bio Tab */}
          {activeTab === 'bio' && (
            <div>
              <div
                style={{
                  backgroundColor: '#161b22',
                  border: '1px solid #30363d',
                  padding: '16px',
                  marginBottom: '16px',
                }}
              >
                <div style={{ color: '#8b949e', fontSize: '9px', marginBottom: '8px' }}>
                  YOUR BIO
                </div>
                <div style={{ color: '#c9d1d9', fontSize: '11px', marginBottom: '12px' }}>
                  Write a short description about yourself. Other players can see this when they inspect your ship.
                </div>
                <textarea
                  value={bioText}
                  onChange={(e) => setBioText(e.target.value.slice(0, 200))}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Enter your bio..."
                  style={{
                    width: '100%',
                    height: '100px',
                    backgroundColor: '#0d1117',
                    border: '1px solid #30363d',
                    color: '#c9d1d9',
                    padding: '10px',
                    fontSize: '12px',
                    fontFamily: '"Sometype Mono", monospace',
                    resize: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ color: '#6e7681', fontSize: '10px', marginTop: '4px', textAlign: 'right' }}>
                  {bioText.length} / 200
                </div>
              </div>

              <button
                onClick={handleUpdateBio}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#238636',
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  fontFamily: '"Sometype Mono", monospace',
                }}
              >
                SAVE BIO
              </button>

              {/* Stats Display */}
              {expressionData && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    YOUR STATS
                  </div>
                  <div
                    style={{
                      backgroundColor: '#161b22',
                      border: '1px solid #30363d',
                      padding: '12px',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px',
                    }}
                  >
                    <div style={{ color: '#8b949e', fontSize: '10px' }}>
                      Kills: <span style={{ color: '#f85149' }}>{expressionData.stats.totalKills}</span>
                    </div>
                    <div style={{ color: '#8b949e', fontSize: '10px' }}>
                      Deaths: <span style={{ color: '#8b949e' }}>{expressionData.stats.totalDeaths}</span>
                    </div>
                    <div style={{ color: '#8b949e', fontSize: '10px' }}>
                      Islands: <span style={{ color: '#3fb950' }}>{expressionData.stats.islandsClaimed}</span>
                    </div>
                    <div style={{ color: '#8b949e', fontSize: '10px' }}>
                      Pieces: <span style={{ color: '#ffd700' }}>{expressionData.stats.totalPiecesEarned.toLocaleString()}</span>
                    </div>
                    <div style={{ color: '#8b949e', fontSize: '10px' }}>
                      Fish: <span style={{ color: '#58a6ff' }}>{expressionData.stats.fishCaught}</span>
                    </div>
                    <div style={{ color: '#8b949e', fontSize: '10px' }}>
                      Playtime: <span style={{ color: '#a371f7' }}>{Math.floor(expressionData.stats.playtimeMinutes / 60)}h {expressionData.stats.playtimeMinutes % 60}m</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
    </div>
  );
};
